import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import chroma from 'chroma-js';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
  arrayMove,
} from 'react-sortable-hoc';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Divider from '@material-ui/core/Divider';
import Snackbar from '@material-ui/core/Snackbar';
import Chip from '@material-ui/core/Chip';
import Drawer from '@material-ui/core/Drawer';
import UndoIcon from '@material-ui/icons/Undo';
import RedoIcon from '@material-ui/icons/Redo';
import AddIcon from '@material-ui/icons/Add';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';
import CloseIcon from '@material-ui/icons/Close';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import HistoryIcon from '@material-ui/icons/History';
import CheckIcon from '@material-ui/icons/Check';
import ViewWeekIcon from '@material-ui/icons/ViewWeek';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import AutorenewIcon from '@material-ui/icons/Autorenew';

import GeneratorColumn from './GeneratorColumn';
import GlobalNav from './components/GlobalNav';
import SavePaletteModal from './components/SavePaletteModal';
import ExportButton from './ExportButton';
import MetaTags from './components/MetaTags';
import { nearestColorName } from './helpers/nameHelpers';
import { calculatePaletteAccessibility } from './helpers/accessibilityHelpers';
import {
  generatePalette,
  createInitialPalette,
  encodeHexString,
  decodeHexString,
  GENERATION_MODES,
  MIN_COLORS,
  MAX_COLORS,
} from './helpers/generateHelpers';
import styles from './styles/GeneratorStyles';

const DragHandle = SortableHandle(({ textColor }) => (
  <Tooltip title="Drag to reorder" placement="top" arrow>
    <IconButton style={{ color: textColor, padding: '8px', cursor: 'grab' }} aria-label="Drag to reorder">
      <DragIndicatorIcon fontSize="small" />
    </IconButton>
  </Tooltip>
));

const SortableColumn = SortableElement(props => (
  <GeneratorColumn {...props} dragHandle={DragHandle} />
));

const SortableColumns = SortableContainer(({ items, names, className, handlers }) => (
  <div className={className}>
    {items.map((color, index) => (
      <SortableColumn
        key={`col-${index}`}
        index={index}
        color={color}
        name={names[index]}
        count={items.length}
        onCopy={handlers.onCopy}
        onToggleLock={() => handlers.onToggleLock(index)}
        onRemove={() => handlers.onRemove(index)}
        onChangeColor={hex => handlers.onChangeColor(index, hex)}
      />
    ))}
  </div>
));

function Generator({
  classes,
  savePalette,
  updatePalette = () => {},
  userPalettes = [],
  projects = [],
  collections = [],
  addProject = () => {},
  addCollection = () => {},
}) {
  const routerHistory = useHistory();
  const params = useParams();
  const location = useLocation();

  const [colors, setColors] = useState([]);
  const [mode, setMode] = useState('auto');
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [snack, setSnack] = useState('');
  const [hintOpen, setHintOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);
  // When a saved user palette is opened in the generator, this holds its
  // metadata so a subsequent Save updates it in place instead of creating new.
  const [editing, setEditing] = useState(null);

  // Refs mirror the latest state for the global keydown handler.
  const colorsRef = useRef([]);
  const modeRef = useRef('auto');
  const historyRef = useRef([]);
  const indexRef = useRef(0);
  const paletteRef = useRef(null);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  // Commit a new palette state to the history stack (truncating any redo tail).
  const commit = useCallback(newColors => {
    const base = historyRef.current.slice(0, indexRef.current + 1);
    const next = [...base, newColors];
    historyRef.current = next;
    indexRef.current = next.length - 1;
    colorsRef.current = newColors;
    setColors(newColors);
    setHistory(next);
    setHistoryIndex(indexRef.current);
  }, []);

  // Update colors in place (live edits / lock toggles) without adding history.
  const replaceCurrent = useCallback(newColors => {
    const hist = [...historyRef.current];
    hist[indexRef.current] = newColors;
    historyRef.current = hist;
    colorsRef.current = newColors;
    setColors(newColors);
    setHistory(hist);
  }, []);

  const setIndex = useCallback(j => {
    indexRef.current = j;
    const c = historyRef.current[j];
    colorsRef.current = c;
    setColors(c);
    setHistoryIndex(j);
  }, []);

  const generate = useCallback(() => {
    const newHexes = generatePalette(modeRef.current, colorsRef.current);
    const newColors = colorsRef.current.map((c, i) => ({ hex: newHexes[i], locked: c.locked }));
    commit(newColors);
  }, [commit]);

  const undo = useCallback(() => {
    if (indexRef.current > 0) setIndex(indexRef.current - 1);
  }, [setIndex]);

  const redo = useCallback(() => {
    if (indexRef.current < historyRef.current.length - 1) setIndex(indexRef.current + 1);
  }, [setIndex]);

  const dismissHint = useCallback(() => {
    setHintOpen(false);
    try {
      window.localStorage.setItem('generatorHintSeen', '1');
    } catch (e) {
      /* ignore */
    }
  }, []);

  // Initial palette: a palette handed in via router state (Open in Generator)
  // takes priority, then the URL hex string, then a fresh random palette.
  useEffect(() => {
    const incoming = location.state && location.state.palette;
    let initial;
    if (incoming && Array.isArray(incoming.colors) && incoming.colors.length >= 2) {
      initial = incoming.colors.map(c => ({ hex: c.color || c.hex, locked: false }));
      if (incoming.isUser && incoming.id) {
        setEditing({
          id: incoming.id,
          paletteName: incoming.paletteName || '',
          description: incoming.description || '',
          tags: incoming.tags || [],
          projectIds: incoming.projectIds || [],
          collectionIds: incoming.collectionIds || [],
          createdAt: incoming.createdAt,
          labels: incoming.colors.map(c => c.name || ''),
        });
      }
    } else {
      initial = decodeHexString(params.hexString) || createInitialPalette(5, 'auto');
    }
    historyRef.current = [initial];
    indexRef.current = 0;
    colorsRef.current = initial;
    setColors(initial);
    setHistory([initial]);
    setHistoryIndex(0);
    try {
      if (!window.localStorage.getItem('generatorHintSeen')) setHintOpen(true);
    } catch (e) {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Global keyboard shortcuts: Space=generate, Left=undo, Right=redo.
  useEffect(() => {
    const handler = e => {
      const el = document.activeElement;
      const tag = el && el.tagName;
      const typing = tag === 'INPUT' || tag === 'TEXTAREA' || (el && el.isContentEditable);
      if (typing) return;
      if (e.code === 'Space') {
        e.preventDefault();
        dismissHint();
        generate();
      } else if (e.key === 'ArrowLeft') {
        undo();
      } else if (e.key === 'ArrowRight') {
        redo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [generate, undo, redo, dismissHint]);

  // Keep the URL in sync with the current palette (refresh-safe & shareable).
  useEffect(() => {
    if (colors.length === 0) return;
    routerHistory.replace(`/generate/${encodeHexString(colors)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colors]);

  const onCopy = useCallback(hex => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(hex).catch(() => {});
    }
    setSnack(`${hex.toUpperCase()} copied to clipboard`);
  }, []);

  const onToggleLock = useCallback(
    index => {
      replaceCurrent(
        colorsRef.current.map((c, i) => (i === index ? { ...c, locked: !c.locked } : c))
      );
    },
    [replaceCurrent]
  );

  const onChangeColor = useCallback(
    (index, hex) => {
      replaceCurrent(colorsRef.current.map((c, i) => (i === index ? { ...c, hex } : c)));
    },
    [replaceCurrent]
  );

  const onRemove = useCallback(
    index => {
      if (colorsRef.current.length <= MIN_COLORS) return;
      commit(colorsRef.current.filter((_, i) => i !== index));
    },
    [commit]
  );

  // Insert a new color between positions i and i+1, mixing the two neighbors.
  const onAddBetween = useCallback(
    i => {
      const cur = colorsRef.current;
      if (cur.length >= MAX_COLORS) {
        setSnack(`Maximum of ${MAX_COLORS} colors reached`);
        return;
      }
      const mid = chroma.mix(cur[i].hex, cur[i + 1].hex, 0.5, 'lab').hex();
      const next = [...cur];
      next.splice(i + 1, 0, { hex: mid, locked: false });
      commit(next);
    },
    [commit]
  );

  const onSortEnd = useCallback(
    ({ oldIndex, newIndex }) => {
      if (oldIndex === newIndex) return;
      commit(arrayMove(colorsRef.current, oldIndex, newIndex));
    },
    [commit]
  );

  const handlePickMode = useCallback(id => {
    setMode(id);
    modeRef.current = id;
    setMenuAnchor(null);
  }, []);

  // Persist a palette from the Save modal. Editing an existing user palette
  // updates it in place (same id); otherwise a new palette is created.
  const doSave = useCallback(
    data => {
      if (editing && editing.id) {
        updatePalette(editing.id, {
          id: editing.id,
          paletteName: data.paletteName,
          emoji: '🎨',
          description: data.description,
          tags: data.tags,
          colors: data.colors,
          projectIds: data.projectIds,
          collectionIds: data.collectionIds,
          createdAt: editing.createdAt || Date.now(),
        });
        setSnack(`Updated "${data.paletteName}"`);
      } else {
        const slug = data.paletteName.toLowerCase().replace(/\s+/g, '-');
        savePalette({
          id: `${slug}-${Date.now()}`,
          paletteName: data.paletteName,
          emoji: '🎨',
          description: data.description,
          tags: data.tags,
          colors: data.colors,
          projectIds: data.projectIds,
          collectionIds: data.collectionIds,
          createdAt: Date.now(),
        });
        setSnack(`Saved "${data.paletteName}"`);
      }
      setSaveOpen(false);
    },
    [savePalette, updatePalette, editing]
  );

  // Load a saved palette back into the generator.
  const loadPalette = useCallback(
    p => {
      const cols = (p.colors || []).map(c => ({ hex: c.color || c.hex, locked: false }));
      if (cols.length >= 2) {
        commit(cols);
        setBrowseOpen(false);
      }
    },
    [commit]
  );

  if (colors.length === 0) return null;

  const names = colors.map(c => nearestColorName(c.hex));
  const a11y = calculatePaletteAccessibility(colors.map(c => ({ name: c.hex, color: c.hex })));

  const exportPalette = {
    paletteName: 'Generated Palette',
    id: 'generated',
    emoji: '🎨',
    colors: colors.map((c, i) => ({ name: names[i] || `color-${i + 1}`, color: c.hex })),
  };

  // One adder per boundary between adjacent columns.
  const adders = colors.slice(0, -1).map((_, i) => (
    <div
      key={`add-${i}`}
      className={classes.adder}
      style={{ left: `${((i + 1) * 100) / colors.length}%` }}
    >
      <Tooltip title="Add a color here" placement="top" arrow>
        <IconButton
          size="small"
          className={classes.adderButton}
          onClick={() => onAddBetween(i)}
          aria-label="Add color between"
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  ));

  return (
    <div className={classes.Generator}>
      <MetaTags
        title="Palette Generator - Custom Color"
        description="Generate beautiful color palettes instantly. Press the spacebar to create new palettes, lock colors you love, and export in multiple formats."
        keywords="color palette generator, spacebar generator, color scheme, random colors, color tools"
        url={window.location.href}
      />
      <GlobalNav />

      <div className={classes.toolbar}>
        <span className={classes.hint}>Press the spacebar to generate color palettes!</span>

        <span className={classes.toolbarSpacer} />

        <div className={classes.iconGroup}>
          <Chip
            size="small"
            className={classes.a11yChip}
            label={`A11y ${a11y.score}%`}
            style={{
              backgroundColor:
                a11y.score >= 70 ? '#2e7d32' : a11y.score >= 40 ? '#ed6c02' : '#c62828',
              color: '#fff',
            }}
          />

          <Tooltip title="More options" arrow>
            <IconButton
              className={classes.toolbarIcon}
              onClick={e => setMenuAnchor(e.currentTarget)}
              aria-label="More options"
            >
              <MoreHorizIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Undo (←)" arrow>
            <span>
              <IconButton
                className={classes.toolbarIcon}
                onClick={undo}
                disabled={historyIndex <= 0}
                aria-label="Undo"
              >
                <UndoIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Redo (→)" arrow>
            <span>
              <IconButton
                className={classes.toolbarIcon}
                onClick={redo}
                disabled={historyIndex >= history.length - 1}
                aria-label="Redo"
              >
                <RedoIcon />
              </IconButton>
            </span>
          </Tooltip>

          <ExportButton palette={exportPalette} paletteElement={paletteRef.current} variant="icon" />

          <Tooltip title="Save palette" arrow>
            <Button
              className={classes.saveButton}
              startIcon={<FavoriteBorderIcon />}
              onClick={() => setSaveOpen(true)}
              aria-label="Save palette"
            >
              <span className={classes.saveLabel}>Save</span>
            </Button>
          </Tooltip>

          <Tooltip title="Browse your palettes" arrow>
            <IconButton
              className={classes.toolbarIcon}
              onClick={() => setBrowseOpen(true)}
              aria-label="Browse palettes"
            >
              <ViewWeekIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem disabled style={{ opacity: 0.7, fontSize: '0.75rem', textTransform: 'uppercase' }}>
          Generation method
        </MenuItem>
        {GENERATION_MODES.map(m => (
          <MenuItem key={m.id} selected={mode === m.id} onClick={() => handlePickMode(m.id)}>
            <ListItemIcon style={{ minWidth: 32 }}>
              {mode === m.id ? <CheckIcon fontSize="small" /> : null}
            </ListItemIcon>
            {m.label}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            setHistoryOpen(v => !v);
            setMenuAnchor(null);
          }}
        >
          <ListItemIcon style={{ minWidth: 32 }}>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          Palettes history
        </MenuItem>
        <Divider />
        <MenuItem disabled style={{ opacity: 0.8 }}>
          Accessibility: {a11y.score}%
        </MenuItem>
      </Menu>

      <div className={classes.colorsWrap}>
        <div ref={paletteRef} style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <SortableColumns
            axis="x"
            useDragHandle
            items={colors}
            names={names}
            className={classes.colors}
            onSortEnd={onSortEnd}
            handlers={{ onCopy, onToggleLock, onRemove, onChangeColor }}
          />
        </div>

        {adders}

        {/* Touch-friendly alternative to the spacebar shortcut: a compact CTA
            centered over the swatches that regenerates the palette. Shown only
            on small screens where a physical keyboard isn't available. */}
        <Tooltip title="Generate palette" arrow>
          <IconButton
            className={classes.generateFab}
            onClick={() => {
              dismissHint();
              generate();
            }}
            aria-label="Generate new palette"
          >
            <AutorenewIcon />
          </IconButton>
        </Tooltip>

        {historyOpen && (
          <div className={classes.historyPanel}>
            <div className={classes.historyHeader}>
              <span className={classes.historyTitle}>Palettes history</span>
              <IconButton size="small" onClick={() => setHistoryOpen(false)} aria-label="Close history">
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
            <div className={classes.historyList}>
              {history
                .map((snap, idx) => ({ snap, idx }))
                .reverse()
                .map(({ snap, idx }) => (
                  <div
                    key={idx}
                    className={`${classes.historyItem} ${
                      idx === historyIndex ? classes.historyItemActive : ''
                    }`}
                    onClick={() => setIndex(idx)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setIndex(idx);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Restore palette ${idx + 1}`}
                  >
                    {snap.map((c, ci) => (
                      <div
                        key={ci}
                        className={classes.historySwatch}
                        style={{ backgroundColor: c.hex }}
                      />
                    ))}
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      <SavePaletteModal
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        swatches={colors.map((c, i) => ({ hex: c.hex, name: names[i] }))}
        projects={projects}
        collections={collections}
        onAddProject={addProject}
        onAddCollection={addCollection}
        onSave={doSave}
        initial={
          editing
            ? {
                name: editing.paletteName,
                description: editing.description,
                tags: editing.tags,
                projectIds: editing.projectIds,
                collectionIds: editing.collectionIds,
                labels: editing.labels,
              }
            : null
        }
      />

      <Drawer anchor="right" open={browseOpen} onClose={() => setBrowseOpen(false)}>
        <div className={classes.browsePanel}>
          <div className={classes.historyHeader}>
            <span className={classes.historyTitle}>Your palettes</span>
            <IconButton size="small" onClick={() => setBrowseOpen(false)} aria-label="Close browse">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div className={classes.browseList}>
            {userPalettes.length === 0 ? (
              <div className={classes.browseEmpty}>
                No saved palettes yet. Hit <strong>♥ Save</strong> to keep one.
              </div>
            ) : (
              <>
                <div className={classes.browseSection}>All palettes</div>
                {userPalettes.map(p => (
                  <button
                    type="button"
                    key={p.id}
                    className={classes.browseItem}
                    onClick={() => loadPalette(p)}
                  >
                    <div className={classes.browseStrip}>
                      {(p.colors || []).map((c, ci) => (
                        <div
                          key={ci}
                          style={{ flex: 1, backgroundColor: c.color || c.hex }}
                        />
                      ))}
                    </div>
                    <span className={classes.browseName}>{p.paletteName}</span>
                  </button>
                ))}

                {projects.map(proj => {
                  const inProject = userPalettes.filter(p =>
                    (p.projectIds || []).includes(proj.id)
                  );
                  if (inProject.length === 0) return null;
                  return (
                    <div key={proj.id}>
                      <div className={classes.browseSection}>
                        <FolderOpenIcon style={{ fontSize: 16, verticalAlign: 'text-bottom' }} />{' '}
                        {proj.name}
                      </div>
                      {inProject.map(p => (
                        <button
                          type="button"
                          key={p.id}
                          className={classes.browseItem}
                          onClick={() => loadPalette(p)}
                        >
                          <div className={classes.browseStrip}>
                            {(p.colors || []).map((c, ci) => (
                              <div
                                key={ci}
                                style={{ flex: 1, backgroundColor: c.color || c.hex }}
                              />
                            ))}
                          </div>
                          <span className={classes.browseName}>{p.paletteName}</span>
                        </button>
                      ))}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </Drawer>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={Boolean(snack)}
        autoHideDuration={2500}
        onClose={() => setSnack('')}
        message={snack}
        action={
          <IconButton size="small" color="inherit" onClick={() => setSnack('')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={hintOpen}
        onClose={dismissHint}
        message="💡 Press the spacebar to generate palettes!"
        action={
          <Button color="secondary" size="small" onClick={dismissHint}>
            Got it
          </Button>
        }
      />
    </div>
  );
}

export default withStyles(styles)(Generator);
